import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart3, FileText, Calendar, Activity, AlertTriangle, CheckCircle, Clock, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { useReportsData } from '@/hooks/useReportsData';
import { useAdvancedReportsData } from '@/hooks/useAdvancedReportsData';
import { useSLAPerformance } from '@/hooks/useSLAPerformance';
import { PageLoading } from '@/components/ui/page-loading';
import { ExecutiveSummary } from './ExecutiveSummary';
import { SLAPerformanceCard } from './SLAPerformanceCard';
import { ProductivityMetrics } from './ProductivityMetrics';
import { TeamPerformanceTable } from './TeamPerformanceTable';
import { ReportFilters } from './ReportFilters';
import { ReportBuilder } from './ReportBuilder';
import { ReportTemplateList } from './ReportTemplateList';
import { exportToPDF, exportToExcel, type ReportData } from '@/utils/reportExport';
import { ReportWidget } from '@/hooks/useReportTemplates';
import { ReportTicketList } from './ReportTicketList';
import { toast } from 'sonner';

const ReportsManagement = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Filters state
  const [startDate, setStartDate] = useState<Date>(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState<Date>(endOfMonth(new Date()));
  const [ticketTypes, setTicketTypes] = useState<string[]>(['incidente', 'requisicao', 'problema', 'mudanca']);
  const [priority, setPriority] = useState('all');
  
  // Custom widget view
  const [customWidgets, setCustomWidgets] = useState<ReportWidget[] | null>(null);

  // Use startDate as selectedMonth for data hooks
  const selectedMonth = startDate;
  
  const { statusData, typeData, monthlyData, metrics, loading } = useReportsData(selectedMonth);
  const { productivityMetrics, teamPerformance, isLoading: advancedLoading } = useAdvancedReportsData(selectedMonth);
  const { data: slaMetrics, isLoading: slaLoading } = useSLAPerformance();

  const isLoading = loading || advancedLoading || slaLoading;

  // Filter data by ticket type
  const filteredTypeData = typeData.filter(t => {
    const typeMap: Record<string, string> = {
      'Incidentes': 'incidente',
      'Requisições': 'requisicao',
      'Problemas': 'problema',
      'Mudanças': 'mudanca',
    };
    return ticketTypes.includes(typeMap[t.name] || '');
  });

  const handleExportPDF = async () => {
    try {
      toast.loading('Gerando PDF...');
      const fileName = `relatorio-tickets-${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}.pdf`;
      await exportToPDF('report-content', fileName, selectedMonth);
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar PDF');
    }
  };

  const handleExportExcel = () => {
    try {
      toast.loading('Gerando Excel...');
      const reportData: ReportData = {
        metrics,
        statusData,
        typeData: filteredTypeData,
        monthlyData,
        productivityMetrics: productivityMetrics ? {
          avgResolutionHours: productivityMetrics.avgResolutionHours,
          resolutionRate: productivityMetrics.resolutionRate,
          sameDay: productivityMetrics.sameDay,
          oneToThreeDays: productivityMetrics.oneToThreeDays,
          threeToSevenDays: productivityMetrics.threeToSevenDays,
          moreThanSevenDays: productivityMetrics.moreThanSevenDays,
          backlogChange: productivityMetrics.backlogChange,
        } : undefined,
        teamPerformance: teamPerformance?.map(t => ({
          name: t.name,
          totalTickets: t.totalTickets,
          resolved: t.resolvedTickets,
          resolutionRate: t.resolutionRate,
          avgResolutionHours: t.avgResolutionHours,
        })),
        slaMetrics: slaMetrics?.map(s => ({
          categoria: s.category,
          total: s.total,
          cumpridos: s.onTime,
          descumpridos: s.breached,
          taxa_cumprimento: s.total > 0 ? (s.onTime / s.total) * 100 : 0,
        })),
        selectedMonth,
      };
      const fileName = `relatorio-tickets-${format(startDate, 'yyyy-MM-dd')}.xlsx`;
      exportToExcel(reportData, fileName);
      toast.success('Excel exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar Excel');
    }
  };

  const handlePreview = (widgets: ReportWidget[]) => {
    setCustomWidgets(widgets);
    setActiveTab('dashboard');
  };

  const isWidgetVisible = (type: string) => {
    if (!customWidgets) return true;
    return customWidgets.some(w => w.type === type && w.enabled);
  };

  const getWidgetOrder = (type: string) => {
    if (!customWidgets) return 0;
    const w = customWidgets.find(w => w.type === type);
    return w?.order ?? 99;
  };

  // Build ordered widget list
  const renderDashboardWidgets = () => {
    if (isLoading) return <PageLoading message="Carregando relatórios..." />;

    const widgetComponents: { type: string; order: number; component: React.ReactNode }[] = [];

    if (isWidgetVisible('executive_summary')) {
      widgetComponents.push({
        type: 'executive_summary',
        order: getWidgetOrder('executive_summary'),
        component: (
          <ExecutiveSummary
            key="executive_summary"
            totalTickets={metrics.totalTickets}
            openTickets={metrics.openTickets}
            resolvedTickets={metrics.resolvedTickets}
            criticalTickets={metrics.criticalTickets}
            resolutionRate={productivityMetrics?.resolutionRate || 0}
            avgResolutionHours={productivityMetrics?.avgResolutionHours || 0}
            slaPerformance={slaMetrics?.[0]?.current || 0}
            backlogChange={productivityMetrics?.backlogChange || 0}
          />
        ),
      });
    }

    if (isWidgetVisible('kpi')) {
      widgetComponents.push({
        type: 'kpi',
        order: getWidgetOrder('kpi'),
        component: (
          <div key="kpi" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total de Tickets</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metrics.totalTickets}</div>
                <p className="text-xs text-muted-foreground">
                  Período: {format(startDate, "dd/MM", { locale: ptBR })} - {format(endDate, "dd/MM", { locale: ptBR })}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Tickets Abertos</CardTitle>
                <Clock className="h-4 w-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">{metrics.openTickets}</div>
                <p className="text-xs text-muted-foreground">
                  {metrics.totalTickets > 0 ? Math.round((metrics.openTickets / metrics.totalTickets) * 100) : 0}% do total
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Tickets Resolvidos</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{metrics.resolvedTickets}</div>
                <p className="text-xs text-muted-foreground">
                  {metrics.totalTickets > 0 ? Math.round((metrics.resolvedTickets / metrics.totalTickets) * 100) : 0}% do total
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Tickets Críticos</CardTitle>
                <AlertTriangle className="h-4 w-4 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{metrics.criticalTickets}</div>
                <p className="text-xs text-muted-foreground">Prioridade crítica</p>
              </CardContent>
            </Card>
          </div>
        ),
      });
    }

    if (isWidgetVisible('sla') && slaMetrics && slaMetrics.length > 0) {
      widgetComponents.push({
        type: 'sla',
        order: getWidgetOrder('sla'),
        component: <SLAPerformanceCard key="sla" slaMetrics={slaMetrics} />,
      });
    }

    if (isWidgetVisible('productivity') && productivityMetrics) {
      widgetComponents.push({
        type: 'productivity',
        order: getWidgetOrder('productivity'),
        component: (
          <ProductivityMetrics
            key="productivity"
            avgResolutionHours={productivityMetrics.avgResolutionHours}
            resolutionRate={productivityMetrics.resolutionRate}
            sameDay={productivityMetrics.sameDay}
            oneToThreeDays={productivityMetrics.oneToThreeDays}
            threeToSevenDays={productivityMetrics.threeToSevenDays}
            moreThanSevenDays={productivityMetrics.moreThanSevenDays}
            backlogChange={productivityMetrics.backlogChange}
          />
        ),
      });
    }

    if (isWidgetVisible('team') && teamPerformance && teamPerformance.length > 0) {
      widgetComponents.push({
        type: 'team',
        order: getWidgetOrder('team'),
        component: <TeamPerformanceTable key="team" teamPerformance={teamPerformance} />,
      });
    }

    if (isWidgetVisible('status_pie') || isWidgetVisible('type_bar')) {
      const charts: React.ReactNode[] = [];
      
      if (isWidgetVisible('status_pie')) {
        charts.push(
          <Card key="status_pie">
            <CardHeader>
              <CardTitle>Distribuição por Status</CardTitle>
              <CardDescription>Status atual dos tickets</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        );
      }

      if (isWidgetVisible('type_bar')) {
        charts.push(
          <Card key="type_bar">
            <CardHeader>
              <CardTitle>Tickets por Tipo</CardTitle>
              <CardDescription>Distribuição por categoria</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={filteredTypeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="total" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        );
      }

      widgetComponents.push({
        type: 'charts',
        order: Math.min(
          isWidgetVisible('status_pie') ? getWidgetOrder('status_pie') : 99,
          isWidgetVisible('type_bar') ? getWidgetOrder('type_bar') : 99
        ),
        component: (
          <div key="charts" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {charts}
          </div>
        ),
      });
    }

    if (isWidgetVisible('timeline')) {
      widgetComponents.push({
        type: 'timeline',
        order: getWidgetOrder('timeline'),
        component: (
          <Card key="timeline">
            <CardHeader>
              <CardTitle>Evolução Diária dos Tickets</CardTitle>
              <CardDescription>
                Tickets criados por dia — {format(startDate, "dd/MM", { locale: ptBR })} a {format(endDate, "dd/MM", { locale: ptBR })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {ticketTypes.includes('incidente') && (
                    <Line type="monotone" dataKey="incidents" stroke="#ef4444" strokeWidth={2} name="Incidentes" />
                  )}
                  {ticketTypes.includes('requisicao') && (
                    <Line type="monotone" dataKey="requests" stroke="#3b82f6" strokeWidth={2} name="Requisições" />
                  )}
                  {ticketTypes.includes('problema') && (
                    <Line type="monotone" dataKey="problems" stroke="#f97316" strokeWidth={2} name="Problemas" />
                  )}
                  {ticketTypes.includes('mudanca') && (
                    <Line type="monotone" dataKey="changes" stroke="#22c55e" strokeWidth={2} name="Mudanças" />
                  )}
                  <Line type="monotone" dataKey="total" stroke="#6b7280" strokeWidth={3} name="Total" strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        ),
      });
    }

    widgetComponents.sort((a, b) => a.order - b.order);

    return (
      <div className="space-y-6">
        {widgetComponents.map(w => w.component)}

        {/* Export */}
        <Card>
          <CardHeader>
            <CardTitle>Exportar Relatórios</CardTitle>
            <CardDescription>Gere relatórios detalhados em diferentes formatos</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button variant="outline" className="gap-2" onClick={handleExportPDF}>
                <FileText className="h-4 w-4" />
                Exportar PDF
              </Button>
              <Button variant="outline" className="gap-2" onClick={handleExportExcel}>
                <BarChart3 className="h-4 w-4" />
                Exportar Excel
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Relatórios</h1>
          <p className="text-muted-foreground">
            Dashboard interativo, relatórios customizados e templates reutilizáveis
          </p>
        </div>
        {customWidgets && activeTab === 'dashboard' && (
          <Button variant="ghost" size="sm" onClick={() => setCustomWidgets(null)}>
            Voltar ao Dashboard completo
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" className="gap-1.5">
            <BarChart3 className="h-4 w-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="builder" className="gap-1.5">
            <FileText className="h-4 w-4" />
            Criar Relatório
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-1.5">
            <Calendar className="h-4 w-4" />
            Meus Relatórios
          </TabsTrigger>
          <TabsTrigger value="ticket-list" className="gap-1.5">
            <List className="h-4 w-4" />
            Lista de Chamados
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6">
          {/* Filters */}
          <ReportFilters
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            ticketTypes={ticketTypes}
            onTicketTypesChange={setTicketTypes}
            priority={priority}
            onPriorityChange={setPriority}
          />

          <div id="report-content">
            {renderDashboardWidgets()}
          </div>
        </TabsContent>

        <TabsContent value="builder">
          <ReportBuilder onPreview={handlePreview} />
        </TabsContent>

        <TabsContent value="templates">
          <ReportTemplateList onLoadTemplate={handlePreview} />
        </TabsContent>

        <TabsContent value="ticket-list" className="space-y-6">
          <ReportFilters
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            ticketTypes={ticketTypes}
            onTicketTypesChange={setTicketTypes}
            priority={priority}
            onPriorityChange={setPriority}
          />
          <ReportTicketList
            startDate={startDate}
            endDate={endDate}
            ticketTypes={ticketTypes}
            priority={priority}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsManagement;
